import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	HeadingLevel,
	If,
	isAfter,
	isBefore,
	makeClassName,
	Modal,
	RelativeTimestamp,
	Row,
	Rule,
	Section,
	StyleProps,
	toLocalDateString,
	TruncatedText,
} from "@hex-insights/core";
import { Badge } from "../../../../Components/Badge";
import { SlimEmptyView } from "../../../../Components/Empty";
import { Tile } from "../../../../Components/Tile";
import {
	CourseSection,
	CourseSectionCourseSectionPeriodScheduleListQuery,
	CourseSectionPeriod,
	CourseSectionPeriodFilterInput,
} from "../../../../Utilities";
import { CourseSectionFormat, CourseSectionPeriodFormat, RoomFormat } from "../../../Format";
import { CourseSectionLink } from "../../../Links";
import { CourseSectionPeriodUtilities } from "../../../Utilities";
import { ControlledCourseAttendanceModal } from "../../CourseAttendanceRecord";
import { ScheduleCalendar } from "../ScheduleCalendar";
import { CourseSectionPeriodStudentsList } from "../StudentsList";
import styles from "./styles.module.css";

export type ScheduleTileProps = {
	headingLevel?: HeadingLevel;
	filters: CourseSectionPeriodFilterInput;
	Container?: typeof Tile | typeof Section;
} & Partial<ClassNameProps & StyleProps>;

export function ScheduleTile({ headingLevel = 2, filters, Container = Tile, className, style }: ScheduleTileProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { loading, previewPeriods } = CourseSectionPeriodUtilities.useDailySchedulePreview(filters);

	return (
		<React.Fragment>
			<Container className={className} style={style}>
				<Container.Header>
					<Row justify="space-between">
						<Heading level={headingLevel} noMargin>
							Schedule
						</Heading>

						<Button variant="link" size="small" onClick={openModal}>
							View Schedule
						</Button>
					</Row>
				</Container.Header>
				<Container.Body>
					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<Conditional>
							<If condition={previewPeriods.length > 0}>
								{previewPeriods.map((period, i) => (
									<React.Fragment key={period.id}>
										<If condition={i > 0}>
											<Rule color="var(--brand-colors--primary--light)" thickness="1px" />
										</If>

										<CourseSectionLink instance={period.courseSection} className="link--no-text-decoration">
											<Row justify="space-between" align="center">
												<Column style={{ width: "100%", minWidth: 0 }}>
													<TruncatedText className={styles["schedule-item__name"]}>
														{CourseSectionFormat.name(period.courseSection)}
													</TruncatedText>

													{period.room && (
														<TruncatedText className={styles["schedule-item__room-name"]}>
															{RoomFormat.name(period.room)}
														</TruncatedText>
													)}
												</Column>

												<TimingStatusBadge courseSectionPeriod={period} />
											</Row>
										</CourseSectionLink>
									</React.Fragment>
								))}
							</If>
							<If condition={loading}>
								<SlimEmptyView>Loading...</SlimEmptyView>
							</If>
							<Else>
								<SlimEmptyView>No courses today</SlimEmptyView>
							</Else>
						</Conditional>
					</Column>
				</Container.Body>
			</Container>

			<Modal.If condition={isModalOpen}>
				<Modal size="large" onClose={closeModal}>
					<Modal.Body style={{ height: "100%" }}>
						<ScheduleCalendar filters={filters} style={{ height: "100%" }} />
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

export type CourseSectionScheduleTileProps = {
	headingLevel?: HeadingLevel;
	courseSectionID: CourseSection["id"];
	Container?: typeof Tile | typeof Section;
} & Partial<ClassNameProps & StyleProps>;

export function CourseSectionScheduleTile({
	headingLevel = 2,
	courseSectionID,
	Container = Tile,
	className,
	style,
}: CourseSectionScheduleTileProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const filters = React.useMemo<CourseSectionPeriodFilterInput>(
		() => ({
			courseSectionIDEQ: courseSectionID,
		}),
		[courseSectionID],
	);

	const { loading, previewPeriods } =
		CourseSectionPeriodUtilities.useCourseSectionDailySchedulePreview(courseSectionID);

	return (
		<React.Fragment>
			<Container className={className} style={style}>
				<Container.Header>
					<Row justify="space-between">
						<Heading level={headingLevel} noMargin>
							Schedule
						</Heading>

						<Button variant="link" size="small" onClick={openModal}>
							View Schedule
						</Button>
					</Row>
				</Container.Header>
				<Container.Body>
					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<Conditional>
							<If condition={previewPeriods.length > 0}>
								{previewPeriods.map((period, i) => (
									<React.Fragment key={period.id}>
										<If condition={i > 0}>
											<Rule color="var(--brand-colors--primary--light)" thickness="1px" />
										</If>

										<CourseSectionScheduleTilePeriod period={period} />
									</React.Fragment>
								))}
							</If>
							<If condition={loading}>
								<SlimEmptyView>Loading...</SlimEmptyView>
							</If>
							<Else>
								<SlimEmptyView>No periods today</SlimEmptyView>
							</Else>
						</Conditional>
					</Column>
				</Container.Body>
			</Container>

			<Modal.If condition={isModalOpen}>
				<Modal size="large" onClose={closeModal}>
					<Modal.Body style={{ height: "100%" }}>
						<ScheduleCalendar filters={filters} style={{ height: "100%" }} />
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type CourseSectionScheduleTilePeriodProps = {
	period: CourseSectionCourseSectionPeriodScheduleListQuery["courseSectionPeriodConnection"]["edges"][0]["node"];
};

function CourseSectionScheduleTilePeriod({ period }: CourseSectionScheduleTilePeriodProps) {
	const hasAttendance = period.courseAttendanceRecords.length > 0;
	const {
		isModalOpen: isAttendanceCreateModalOpen,
		openModal: openAttendanceCreateModal,
		closeModal: closeAttendanceCreateModal,
	} = Modal.useToggle();
	const {
		isModalOpen: isAttendanceUpdateModalOpen,
		openModal: openAttendanceUpdateModal,
		closeModal: closeAttendanceUpdateModal,
	} = Modal.useToggle();

	const date = toLocalDateString(new Date());

	return (
		<Row justify="space-between" align="center">
			<Column style={{ width: "100%", minWidth: 0 }}>
				<TruncatedText className={styles["schedule-item__name"]}>
					{CourseSectionPeriodFormat.Fields.startTimeMinutes(period.startTimeMinutes)} -{" "}
					{CourseSectionPeriodFormat.Fields.endTimeMinutes(period.endTimeMinutes)}
				</TruncatedText>

				{period.room && (
					<TruncatedText className={styles["schedule-item__room-name"]}>{RoomFormat.name(period.room)}</TruncatedText>
				)}

				<Row justify="spaced-start" align="center">
					<span
						className={
							styles["schedule-item__attendance"] +
							" " +
							styles[`schedule-item__attendance--${hasAttendance ? "taken" : "not-taken"}`] +
							" nowrap"
						}
					>
						{hasAttendance ? "Attendance Taken" : "No Attendance Taken"}
					</span>

					<Button
						variant="link"
						size="small"
						onClick={hasAttendance ? openAttendanceUpdateModal : openAttendanceCreateModal}
						style={{ paddingTop: 0, paddingBottom: 0 }}
					>
						{hasAttendance ? "Edit" : "Take"}
					</Button>

					<Modal.If condition={isAttendanceCreateModalOpen}>
						<ControlledCourseAttendanceModal
							courseSectionPeriodID={period.id}
							date={date}
							onClose={closeAttendanceCreateModal}
						/>
					</Modal.If>
					<Modal.If condition={isAttendanceUpdateModalOpen}>
						<Modal size="large" onClose={closeAttendanceUpdateModal}>
							<Modal.Header>
								<Heading.H2 noMargin>Update Attendance</Heading.H2>
							</Modal.Header>
							<Modal.Body
								style={{
									height: "100%",
									paddingTop: "0.5rem",
									overflow: "scroll",
									display: "flex",
									flexDirection: "column",
								}}
							>
								<CourseSectionPeriodStudentsList courseSectionPeriodID={period.id} date={date} />
							</Modal.Body>
						</Modal>
					</Modal.If>
				</Row>
			</Column>

			<TimingStatusBadge courseSectionPeriod={period} />
		</Row>
	);
}

type TimingStatusBadgeProps = {
	courseSectionPeriod: Pick<CourseSectionPeriod, "startTimeMinutes" | "endTimeMinutes">;
} & Partial<ClassNameProps & StyleProps>;

function TimingStatusBadge({ courseSectionPeriod, className, style }: TimingStatusBadgeProps) {
	const date = new Date();

	const startTime = CourseSectionPeriodUtilities.minutesToDate(courseSectionPeriod.startTimeMinutes, date);
	const endTime = CourseSectionPeriodUtilities.minutesToDate(courseSectionPeriod.endTimeMinutes, date);
	let status = "current";
	if (isBefore(date, startTime)) {
		status = "upcoming";
	} else if (isAfter(date, endTime)) {
		status = "past";
	}

	return (
		<Badge
			size="small"
			className={makeClassName(styles["status-badge"], styles[`status-badge--${status}`], className)}
			style={style}
		>
			<Conditional>
				<If condition={status === "current"}>happening now</If>
				<If condition={status === "upcoming"}>
					<RelativeTimestamp value={startTime} />
				</If>
				<If condition={status === "past"}>
					<RelativeTimestamp value={endTime} />
				</If>
			</Conditional>
		</Badge>
	);
}
